import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCcw, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { listApplications, markApplicationPaid } from "@/lib/api";

function StatusBadge({ status }) {
  const map = {
    paid: "text-[#A67C2E] border-[#A67C2E]/50 bg-[#A67C2E]/10",
    pending: "text-[#B45309] border-[#B45309]/40 bg-[#F59E0B]/10",
    unpaid: "text-[#6B6560] border-[#DDD1BC] bg-[#F5EFE4]",
    expired: "text-[#B91C1C] border-[#B91C1C]/40 bg-[#FEE2E2]",
    failed: "text-[#B91C1C] border-[#B91C1C]/40 bg-[#FEE2E2]",
  };
  const cls = map[status] || map.unpaid;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] uppercase tracking-widest ${cls}`}
      data-testid={`status-badge-${status}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {status}
    </span>
  );
}

export default function Admin() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [markingId, setMarkingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listApplications();
      setApps(data);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Mark this application as paid? This will send confirmation emails.")) {
      return;
    }
    setMarkingId(id);
    try {
      const updated = await markApplicationPaid(id);
      setApps((list) => list.map((a) => (a.id === id ? updated : a)));
      if (selected?.id === id) setSelected(updated);
      toast.success("Application marked paid — emails sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark paid.");
    } finally {
      setMarkingId(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF6F0] text-[#2A2521]" data-testid="admin-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAF6F0]/80 border-b border-[#E8DCC8]/60">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#4A423B] hover:text-[#2A2521]"
            data-testid="admin-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-heading text-lg tracking-wide">Experian Properties · Admin</span>
          </Link>
          <button
            onClick={load}
            className="btn-ghost-gold inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm"
            data-testid="admin-refresh-btn"
          >
            <RefreshCcw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-10">
          <p className="label-eyebrow text-[#A67C2E] mb-3">Applications</p>
          <h1 className="font-heading text-3xl md:text-4xl">
            {apps.length} submission{apps.length === 1 ? "" : "s"}
          </h1>
        </div>

        <div className="rounded-sm border border-[#E8DCC8] overflow-hidden bg-white">
          <table className="w-full text-sm" data-testid="admin-applications-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-[#8B8378] border-b border-[#E8DCC8]/60">
                <th className="px-6 py-4 font-normal">Applicant</th>
                <th className="px-6 py-4 font-normal">Property</th>
                <th className="px-6 py-4 font-normal">Email</th>
                <th className="px-6 py-4 font-normal">Submitted</th>
                <th className="px-6 py-4 font-normal">Status</th>
                <th className="px-6 py-4 font-normal"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-[#8B8378]">
                    Loading…
                  </td>
                </tr>
              ) : apps.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-[#8B8378]">
                    No applications yet.
                  </td>
                </tr>
              ) : (
                apps.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b border-[#E8DCC8]/60 last:border-0 hover:bg-[#F5EFE4]/60 transition"
                    data-testid={`admin-row-${a.id}`}
                  >
                    <td className="px-6 py-4 text-[#2A2521]">{a.full_name}</td>
                    <td className="px-6 py-4 text-[#4A423B] text-xs max-w-[220px] truncate" title={a.property_applying_for}>
                      {a.property_applying_for || "—"}
                    </td>
                    <td className="px-6 py-4 text-[#6B6560]">{a.email}</td>
                    <td className="px-6 py-4 text-[#8B8378] text-xs">
                      {new Date(a.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={a.payment_status} />
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      {a.payment_status !== "paid" && (
                        <button
                          onClick={(e) => handleMarkPaid(a.id, e)}
                          disabled={markingId === a.id}
                          className="text-[#A67C2E] hover:text-[#8B6820] text-xs uppercase tracking-widest mr-4 disabled:opacity-50"
                          data-testid={`admin-mark-paid-${a.id}`}
                        >
                          {markingId === a.id ? (
                            <span className="inline-flex items-center gap-1">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              Marking…
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Mark Paid
                            </span>
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => setSelected(a)}
                        className="text-[#6B6560] hover:text-[#2A2521] text-xs uppercase tracking-widest"
                        data-testid={`admin-view-${a.id}`}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center px-4 py-10 overflow-y-auto"
          onClick={() => setSelected(null)}
          data-testid="admin-detail-modal"
        >
          <div
            className="max-w-2xl w-full bg-white border border-[#DDD1BC] rounded-sm p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <p className="label-eyebrow text-[#A67C2E]">Application</p>
                <h2 className="font-heading text-2xl mt-2">{selected.full_name}</h2>
              </div>
              <div className="flex items-center gap-3">
                {selected.payment_status !== "paid" && (
                  <button
                    onClick={(e) => handleMarkPaid(selected.id, e)}
                    disabled={markingId === selected.id}
                    className="btn-gold inline-flex items-center gap-2 px-4 py-2 rounded-sm text-xs disabled:opacity-50"
                    data-testid="modal-mark-paid-btn"
                  >
                    {markingId === selected.id ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Marking…
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Mark Paid
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={() => setSelected(null)}
                  className="text-[#6B6560] hover:text-[#2A2521] text-sm"
                  data-testid="admin-close-modal"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 text-sm">
              <Detail label="Property Applying For" value={selected.property_applying_for} full />
              <Detail label="Lease Length" value={`${selected.lease_length_months || 12} months`} />
              <Detail label="Preferred Move-in" value={selected.preferred_move_in_date} />
              <Detail label="Email" value={selected.email} />
              <Detail label="Phone" value={selected.phone} />
              <Detail label="DOB" value={selected.date_of_birth} />
              <Detail label="Adults" value={selected.number_of_adults} />
              <Detail label="Pets" value={selected.has_pets ? `Yes — ${selected.pet_breeds}` : "No"} />
              <Detail label="Payment" value={selected.payment_status} />
              <Detail label="Address" value={selected.current_address} full />
              <Detail label="Landlord" value={selected.current_landlord_phone} />
              <Detail label="Years there" value={selected.years_at_current_address} />
              <Detail label="Occupation" value={selected.occupation} />
              <Detail label="Job Title" value={selected.job_title} />
              <Detail label="Supervisor" value={`${selected.supervisor_name} · ${selected.supervisor_phone}`} />
              <Detail label="Monthly Income" value={`$${Number(selected.monthly_income).toLocaleString()}`} />
              <Detail
                label="Ref 1"
                value={`${selected.reference_1?.name} · ${selected.reference_1?.phone} · ${selected.reference_1?.relation}`}
                full
              />
              <Detail
                label="Ref 2"
                value={`${selected.reference_2?.name} · ${selected.reference_2?.phone} · ${selected.reference_2?.relation}`}
                full
              />
              <Detail
                label="Ref 3"
                value={`${selected.reference_3?.name} · ${selected.reference_3?.phone} · ${selected.reference_3?.relation}`}
                full
              />
              <Detail label="Felony (7yr)" value={selected.convicted_felony_7yrs ? "Yes" : "No"} />
              {selected.convicted_felony_7yrs && (
                <Detail label="Felony Explanation" value={selected.felony_explanation} full />
              )}
              <Detail label="Prior Eviction" value={selected.evicted_or_asked_to_vacate ? "Yes" : "No"} />
              {selected.evicted_or_asked_to_vacate && (
                <Detail label="Explanation" value={selected.eviction_explanation} full />
              )}
              <Detail
                label="Certification"
                value={
                  selected.certification_agreed
                    ? "Signed — authorized background check & reference contact"
                    : "Not signed"
                }
                full
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <p className="text-[11px] uppercase tracking-widest text-[#8B8378] mb-1">{label}</p>
      <p className="text-[#3A342E] break-words">{value || "—"}</p>
    </div>
  );
}
